import React, { useState } from 'react';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Card from '../components/common/Card';
import Alert from '../components/common/Alert';
import IconButton from '../components/common/IconButton';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    React.useEffect(() => {
        if (user) {
            navigate('/admin', { replace: true });
        }
    }, [user, navigate]);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const success = await login(username, password);
            if (success) {
                navigate('/admin');
            }
        } catch (err) {
            if (err.message === 'Invalid credentials') {
                setError('Invalid credentials. Please check your username and password.');
            } else {
                console.error('Login technical failure:', err);
                navigate('/error');
            }
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
            <Card className="w-full max-w-sm border border-slate-100 dark:border-slate-800">
                <div className="text-center mb-6">
                    <div className="flex justify-center items-center gap-2 mb-4">
                        <div className="flex flex-col items-center">
                            <img
                                src="/logo.jpg"
                                alt="Yaseero Logo"
                                className="h-14 object-contain mb-2 rounded-xl"
                            />
                            <img
                                src="/name.jpg"
                                alt="Yaseero"
                                className="h-7 object-contain"
                            />
                        </div>
                    </div>
                </div>

                <form onSubmit={handleLogin} className="space-y-5">
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Welcome Back</h2>
                        <p className="text-slate-400 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider mt-1">Please sign in to your account</p>
                    </div>

                    {error && (
                        <Alert variant="danger">
                            {error}
                        </Alert>
                    )}

                    <div className="space-y-4">
                        <Input
                            label="Username or Email"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            icon={User}
                            required
                        />
                        <Input
                            label="Password"
                            placeholder="Enter your password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            icon={Lock}
                            required
                            rightElement={
                                <IconButton
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    icon={showPassword ? EyeOff : Eye}
                                    ariaLabel={showPassword ? 'Hide password' : 'Show password'}
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                                />
                            }
                        />
                    </div>

                    <Button
                        type="submit"
                        isLoading={isLoading}
                        className="w-full h-12 text-base font-bold shadow-lg shadow-blue-500/20"
                        size="md"
                    >
                        Sign In
                    </Button>
                </form>

                <div className="mt-6 text-center text-sm space-y-2">
                    <a href="#" className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 font-semibold block rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                        Forgot password?
                    </a>
                    <div className="text-slate-400 dark:text-slate-500 font-semibold">
                        Don't have an account?{' '}
                        <button
                          onClick={() => navigate('/register')}
                          className="text-blue-600 dark:text-sky-400 hover:underline font-bold rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          Sign Up
                        </button>
                    </div>
                </div>

                <div className="mt-8 text-center border-t border-slate-50 dark:border-slate-700/50 pt-6">
                    <p className="text-slate-400 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider">simple ordering for smart suppliers</p>
                </div>
            </Card>
        </div>
    );
};

export default LoginPage;
